<template>
  <div id="repository-curriculum">
    <div
      class="sticky top-0 z-50 flex items-center flex-shrink-0 h-12 px-6 bg-pure-white"
    >
      <BlBreadcrumbs :breadcrumbs="breadcrumbs" />
    </div>
    <div v-if="dataLoading" class="grid h-192 place-items-center">
      <div class="blum-loader"></div>
    </div>
    <div v-else-if="curriculum">
      <div
        id="curriculum-header"
        class="flex flex-col gap-1 px-6 py-4 border-b bg-pure-white border-slate-400"
      >
        <div class="flex gap-2">
          <div
            v-if="curriculum.color"
            class="flex-shrink-0 w-6 h-6 mt-1 rounded-full"
            :style="{ backgroundColor: curriculum.color }"
          />
          <BlTextTitle4 :text="curriculum.name" />
          <div class="flex items-center gap-2">
            <BlBubbleCount :count="totalDatabankTargets || 0" />
            <BlInputButton
              v-if="curriculum.description"
              type="outlined"
              dense
              :label="'View Description'"
              @clicked="viewOpened = true"
            />
            <BlInputButton
              v-else-if="currentUser.role.includes('admin')"
              type="outlined"
              dense
              :label="'Add Description'"
              @clicked="editOpened = true"
            />
          </div>
        </div>
        <BlTextBody2
          class="whitespace-pre-line line-clamp-3"
          :text="curriculum.description"
        />
      </div>
      <BlBackdrop
        v-if="viewOpened"
        label="Close"
        :cancelLabel="currentUser.role.includes('admin') ? 'Edit' : ''"
        @backdrop-close="onOpenEdit"
        @backdrop-action="viewOpened = false"
      >
        <div class="flex gap-2 px-8 pt-8 pb-4">
          <div
            v-if="curriculum.color"
            class="flex-shrink-0 w-6 h-6 mt-1 rounded-full"
            :style="{ backgroundColor: curriculum.color }"
          />
          <BlTextTitle4 :text="curriculum.name" />
        </div>
        <div
          class="relative flex flex-col gap-1 px-8 overflow-y-auto w-160"
          :style="{ maxHeight: 'calc(100vh - 14rem)' }"
        >
          <div class="flex flex-col">
            <BlTextBody2
              :text="curriculum.description"
              class="flex-wrap break-words whitespace-pre-line"
            />
          </div>
        </div>
      </BlBackdrop>
      <EditCurriculumModal
        v-if="editOpened"
        :curriculum="curriculum"
        @close-modal="editOpened = false"
      />
      <div v-if="!currentUser.role.includes('admin')" class="w-full px-6">
        <div class="flex items-center gap-2 px-4 py-2 rounded-b bg-info-100">
          <div
            class="grid flex-shrink-0 w-10 h-10 place-items-center"
            :style="{ background: '#E4F1FA', borderRadius: '0.25rem' }"
          >
            <BlIcon icon="toast-info" class="w-6 h-6" />
          </div>
          <BlTextBody1
            class="text-info"
            text="Please note that adding or editing the target databank is restricted to the admin role."
          />
        </div>
      </div>
      <div class="flex items-center gap-4 px-6 py-4 bg-slate-100">
        <BlInputSearch
          class="w-80"
          :input="searchInput"
          @changed="onInputSearch"
        />
        <div class="w-40">
          <BlInputFilter :input="filterInput" @changed="onChangeFilter" />
        </div>
        <BlSort
          class="w-60"
          v-model="sortInput"
          :options="sortOptions"
          :defaultValue="{ primary: 'date', secondary: 'desc' }"
          @change="onChangeSort($event)"
        />
      </div>
      <div v-if="pageLoading" class="grid h-128 place-items-center">
        <div class="blum-loader"></div>
      </div>
      <div
        v-else-if="!databankTargets.length"
        class="flex flex-col items-center justify-center h-128"
      >
        <BlTextBody1
          class="text-center"
          text="This curriculum is currently empty because no targets associated with it have been added to the databank."
        />
        <BlTextBody1
          class="text-center"
          text="Once you create a target, it will appear here and in the databank."
        />
        <BlInputButton
          label="Create a new target for this curriculum"
          class="p-0"
          type="plain"
          @clicked="newTargetOpened = true"
        />
      </div>
      <div v-else class="flex flex-col gap-6 px-6 pb-40">
        <div class="border rounded border-slate-400">
          <div
            class="sticky z-10 flex items-center flex-grow-0 w-full bg-slate-300"
            :style="{ top: '40px' }"
          >
            <div
              class="flex items-center flex-shrink-0 h-12 px-4 border-r rounded-tl border-slate-400"
              :style="{ width: '20%' }"
            >
              <BlTextSubtitle3 text="Name" />
            </div>
            <div
              class="flex items-center flex-shrink-0 h-12 px-4"
              :style="{ width: '40%' }"
            >
              <BlTextSubtitle3 text="Description" />
            </div>
            <div
              class="flex items-center flex-shrink-0 h-12 px-4"
              :style="{ width: '15%' }"
            >
              <BlTextSubtitle3 text="Data collection method" />
            </div>
            <div
              class="flex items-center flex-shrink-0 h-12 px-4"
              :style="{ width: '15%' }"
            >
              <BlTextSubtitle3 :text="`Goal & success metric`" />
            </div>
            <div
              v-if="currentUser.role.includes('admin')"
              class="flex items-center flex-shrink-0 h-12 px-4 rounded-tr"
              :style="{ width: '10%' }"
            >
              <BlTextSubtitle3 text="" />
            </div>
          </div>
          <SuperAdminDatabankItem
            v-for="target in databankTargets"
            :key="target.id"
            :panelId="panelId"
            :target="target"
            :useAction="currentUser.role.includes('admin')"
            disabledUpdateCurriculum
            @select="onOpenPanel(target)"
          />
        </div>
        <BlInputPagination
          v-if="totalDatabankTargets > 25"
          :page="page"
          :perPage="perPage"
          :total="totalDatabankTargets"
          @changed="changePage"
          @changed-per-page="changePerPage"
        />
      </div>
      <div
        :data-id="panelId"
        id="panel-details"
        class="fixed flex flex-col justify-between overflow-y-auto transition-all duration-300 border top-18 w-92 bg-pure-white border-slate-400"
        :class="[panelOpened ? 'right-0' : '-right-92']"
        :style="{ height: 'calc(100vh - 4.5rem)', zIndex: 51 }"
      >
        <div v-if="panelLoading" class="grid h-full place-items-center">
          <div class="blum-loader"></div>
        </div>
        <SuperAdminDatabankPanelDetails
          v-else-if="selectedTarget"
          :data-id="panelId"
          :target="selectedTarget"
          :panelId="panelId"
        />
        <div
          v-if="currentUser.role.includes('admin') && selectedTarget"
          :data-id="panelId"
          class="sticky bottom-0 px-6 py-5 bg-pure-white"
        >
          <BlInputButton
            :data-id="panelId"
            class="w-full"
            type="outlined"
            :label="'Edit Target'"
            @clicked="editTargetOpened = selectedTarget.id"
          />
        </div>
      </div>
      <SuperAdminEditDatabankModal
        v-if="editTargetOpened"
        :targetId="editTargetOpened"
        @close-modal="editTargetOpened = null"
      />
      <div
        v-if="currentUser.role.includes('admin')"
        class="fixed bottom-0 right-0 z-50 p-6"
      >
        <button
          class="flex flex-col items-center justify-center gap-1 border-2 rounded-full shadow-lg outline-none h-25 w-25 bg-light-purple hover:bg-dark-purple-200 border-pure-white"
          title="New Target Databank"
          @click.prevent="newTargetOpened = true"
        >
          <BlIcon icon="plus" color="secondary" />
          <BlTextSubtitle3 class="text-pure-white" text="New" />
        </button>
      </div>
      <SuperAdminNewTargetModal
        v-if="newTargetOpened"
        title="Create new target for databank"
        kind="databank"
        :defaultCurriculum="curriculum"
        @close-modal="newTargetOpened = false"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash/debounce";

import EditCurriculumModal from "../../../repository/curricula/EditCurriculumModal.vue";
import SuperAdminDatabankItem from "../../databank/Item.vue";
import SuperAdminDatabankPanelDetails from "../../databank/PanelDetails.vue";
import SuperAdminEditDatabankModal from "../../databank/EditDatabankModal.vue";
import SuperAdminNewTargetModal from "../../databank/NewTargetModal.vue";

import BlBubbleCount from "../../../BlBubbleCount.vue";
import BlInputButton from "../../../BlInputButton.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlTextTitle4 from "../../../BlTextTitle4.vue";
import BlTextBody1 from "../../../BlTextBody1.vue";
import BlInputSearch from "../../../BlInputSearch.vue";
import BlTextSubtitle3 from "../../../BlTextSubtitle3.vue";
import BlBreadcrumbs from "../../../BlBreadcrumbs.vue";
import BlBackdrop from "../../../BlBackdrop.vue";
import BlInputFilter from "../../../BlInputFilter.vue";
import BlIcon from "../../../BlIcon.vue";
import BlInputPagination from "../../../BlInputPagination.vue";
import BlSort from "../../../BlSort.vue";

export default {
  name: "SuperAdminDatabankCurriculum",
  components: {
    EditCurriculumModal,
    SuperAdminDatabankItem,
    SuperAdminDatabankPanelDetails,
    SuperAdminEditDatabankModal,
    SuperAdminNewTargetModal,
    BlTextTitle4,
    BlBubbleCount,
    BlInputButton,
    BlTextBody2,
    BlTextBody1,
    BlInputSearch,
    BlTextSubtitle3,
    BlBreadcrumbs,
    BlBackdrop,
    BlInputFilter,
    BlIcon,
    BlInputPagination,
    BlSort
  },
  data() {
    return {
      viewOpened: false,
      editOpened: false,
      panelId: "databank-panel",
      panelOpened: false,
      panelLoading: false,
      selectedTarget: null,
      newTargetOpened: false,
      editTargetOpened: null,
      dataLoading: false,
      pageLoading: true,
      page: 1,
      perPage: 25,
      searchInput: {
        placeholder: "Search target by name",
        model: ""
      },
      filterInput: {
        model: [[]],
        tabs: [
          {
            name: "Method",
            kind: "multi",
            options: [
              { name: "Percentage", value: "Target::Percentage" },
              { name: "Duration", value: "Target::Duration" },
              { name: "Partial interval recording", value: "Target::Pir" },
              { name: "Frequency", value: "Target::Frequency" },
              { name: "Prompting", value: "Target::Prompting" }
            ]
          }
        ]
      },
      sortInput: { primary: "date", secondary: "desc" },
      sortOptions: {
        primary: [
          { value: "name", label: "Name" },
          { value: "type", label: "Data collection method" },
          { value: "date", label: "Date created" }
        ],
        secondary: [
          { value: "asc", label: "Ascending" },
          { value: "desc", label: "Descending" }
        ]
      }
    };
  },
  watch: {
    panelOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    }
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "curriculum",
      "databankTargets",
      "totalDatabankTargets",
      "databank"
    ]),
    breadcrumbs() {
      const curriculumId = Number(this.$route.params.curriculum_id);
      const databankLabel =
        (this.databank && this.databank.id) !== Number(this.$route.params.id)
          ? "Databank name"
          : this.databank.name;
      const curriculumLabel =
        (this.curriculum && this.curriculum.id) !== curriculumId
          ? "Currirulum name"
          : this.curriculum.name;
      return [
        { label: "Shared databanks", target: "/super-admin/databank" },
        {
          label: databankLabel,
          target: `/super-admin/databank/${this.$route.params.id}/curriculum`
        },
        {
          label: curriculumLabel,
          target: `/super-admin/databank/${this.$route.params.id}/curriculum/${curriculumId}`,
          truncate: true
        }
      ];
    },
    getParams() {
      let params = `?page=${this.page}&per_page=${this.perPage}`;
      const query = this.searchInput.model;
      const type = this.filterInput.model
        .at(0)
        .map(a => a.value)
        .join(",");
      const sort = this.sortInput;
      if (query) params += `&query=${query}`;
      if (type) params += `&type=${type}`;
      // - sort mapping -
      // name, ascending -> name_asc
      // name, descending -> name_desc
      // data collection method, ascending -> type_asc
      // data collection method, descending -> type_desc
      // date created, ascending -> oldest
      // date created, descending -> newest
      if (sort.primary === "date") {
        params += `&sort=${sort.secondary === "desc" ? "newest" : "oldest"}`;
      } else {
        params += `&sort=${sort.primary}_${sort.secondary}`;
      }
      return params;
    }
  },
  async mounted() {
    this.dataLoading = true;
    const searchParams = this.$route.query;
    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }
    if (searchParams["query"]) {
      this.searchInput.model = searchParams["query"];
    }
    const filter = [[]];
    if (searchParams["type"]) {
      const types = searchParams["type"].split(",").map(a => {
        const found = this.filterInput.tabs[0].options.find(i => i.value === a);
        return found || { value: a };
      });
      filter[0] = types;
    }
    this.filterInput.model = filter;
    if (searchParams["sort"]) {
      const temp = searchParams["sort"] || "";
      const sorts = {
        name_asc: { primary: "name", secondary: "asc" },
        name_desc: { primary: "name", secondary: "desc" },
        type_asc: { primary: "type", secondary: "asc" },
        type_desc: { primary: "type", secondary: "desc" },
        oldest: { primary: "date", secondary: "asc" },
        newest: { primary: "date", secondary: "desc" }
      };
      if (temp && sorts[temp]) {
        this.sortInput = sorts[temp];
      }
    }

    const databankId = Number(this.$route.params.id);
    await this.getDatabank({ id: databankId });
    this.fetchCurriculum();
    this.fetchTargets();
  },
  methods: {
    ...mapActions(["getCurriculum", "getTargets", "getTarget", "getDatabank"]),
    fetchCurriculum() {
      this.dataLoading = true;
      this.getCurriculum({ id: this.$route.params.curriculum_id }).then(() => {
        this.dataLoading = false;
      });
    },
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.page = 1;
      this.fetchTargets();
    }, 1000),
    onChangeFilter(payload) {
      payload.input.model = payload.value;
      this.page = 1;
      this.fetchTargets();
    },
    onChangeSort(val) {
      const { primary, secondary } = this.sortInput;
      if (val.primary !== primary || val.secondary !== secondary) {
        this.sortInput = val;
        this.page = 1;
        this.fetchTargets();
      }
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchTargets();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchTargets();
    },
    fetchTargets() {
      this.pageLoading = true;
      const params = `?databank_id=${this.$route.params.id}&${this.getParams}&curriculum_ids=${this.$route.params.curriculum_id}`;
      this.getTargets({ kind: "databank", params }).then(() => {
        this.pageLoading = false;
      });

      const incomingPath = `/super-admin/databank/${this.$route.params.id}/curriculum/${this.$route.params.curriculum_id}?${this.getParams}`;
      if (this.$route.fullPath !== incomingPath) {
        this.$router.push(incomingPath).catch(err => {
          if (err.name !== "NavigationDuplicated") {
            throw err;
          }
        });
      }
    },
    onOpenEdit() {
      if (this.currentUser.role.includes("admin")) {
        this.viewOpened = false;
        this.editOpened = true;
      }
    },
    async onOpenPanel(target) {
      const timeout = this.selectedTarget ? 300 : 0;
      setTimeout(async () => {
        this.panelOpened = true;
        this.panelLoading = true;
        const params = { kind: "databank", id: target.id };
        const { data } = await this.getTarget(params);
        this.panelLoading = false;
        this.selectedTarget = data;
      }, timeout);
    },
    onClosePanel() {
      this.panelOpened = false;
      setTimeout(() => {
        this.selectedTarget = null;
      }, 300);
    },
    closeHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (id === this.panelId) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closeHandler);
        this.onClosePanel();
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/superAdmin/databank/curricula/Curriculum.vue